import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  ReactNode,
  useEffect,
} from 'react';
import { gekkoApi } from '../../api/GekkoApi';
import type { AttachmentUpdateMessage } from '../../hooks/useWebSocket';
import { useCases } from './CasesContext';

// Attachment type based on OpenAPI schema
export interface Attachment {
  id: string;
  filename: string;
  mime_type: string;
  size: number | null;
  status: 'PENDING_UPLOAD' | 'UPLOADED' | 'PROCESSING' | 'PROCESSED' | 'ERROR';
  created_at: string;
  updated_at: string;
  upload_url?: string | null;
  download_url?: string | null;
  extracted_text?: string | null;
  error?: string | null;
}

interface CaseAttachmentContextType {
  attachments: Attachment[];
  ensureAttachment: (id: string) => Promise<Attachment | undefined>;
}

const CaseAttachmentContext = createContext<
  CaseAttachmentContextType | undefined
>(undefined);

export const CaseAttachmentProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const attachmentsRef = useRef(attachments);
  attachmentsRef.current = attachments;

  // Get the cases context to register for attachment updates
  const { onAttachmentUpdate } = useCases();

  // Fetch attachment by id and add to state if not present
  const ensureAttachment = useCallback(async (id: string) => {
    const found = attachmentsRef.current.find((a) => a.id === id);
    if (found) return found;
    try {
      const response = await gekkoApi.get<Attachment>(
        `/cases/attachments/${id}`
      );
      if (response) {
        setAttachments((prev) => {
          if (prev.find((a) => a.id === response.id)) return prev;
          return [...prev, response];
        });
        return response;
      }
    } catch (e) {
      // ignore
    }
    return undefined;
  }, []);

  // Handle attachment updates from WebSocket
  const handleAttachmentUpdate = useCallback(
    (message: AttachmentUpdateMessage) => {
      if (message?.type === 'attachment_update' && message.attachment) {
        setAttachments((prev) => {
          const idx = prev.findIndex((a) => a.id === message.attachment.id);
          if (idx === -1) return [...prev, message.attachment];
          const updated = [...prev];
          updated[idx] = { ...updated[idx], ...message.attachment };
          return updated;
        });
      }
    },
    []
  );

  // Register for attachment updates when component mounts
  useEffect(() => {
    const unsubscribe = onAttachmentUpdate(handleAttachmentUpdate);
    return unsubscribe;
  }, [onAttachmentUpdate, handleAttachmentUpdate]);

  const value = { attachments, ensureAttachment };
  return (
    <CaseAttachmentContext.Provider value={value}>
      {children}
    </CaseAttachmentContext.Provider>
  );
};

export const useCaseAttachmentContext = () => {
  const ctx = useContext(CaseAttachmentContext);
  if (!ctx) {
    throw new Error(
      'useCaseAttachmentContext must be used within CaseAttachmentProvider'
    );
  }
  return ctx;
};
